import { Icon } from "@bms-ai/bms-ai-ui-lib/components";
import { useState } from "react";
import FailIcon from "../../../_assets/icons/FailIcon";
import WarningIcon from "../../../_assets/icons/WarningIcon";
import { formatCheckId } from "../../../helpers/helpers";
import { formatMessage } from "../QAResultsNew";

// Render finding message with proper formatting for all check types
const renderFindingMessage = (finding: Finding): React.ReactNode => {
  if (!finding.message) {
    return null;
  }

  // Use formatMessage for figma_default_check
  if (finding.check_id?.toLowerCase() === "figma_font_size_overall_check") {
    return formatMessage(finding.message);
  }

  try {
    const data = JSON.parse(finding.message);

    // Handle array-based JSON (applies to all check types including figma_font_size_check)
    if (Array.isArray(data) && data.length > 0) {
      const firstItem = data[0];

      // Check if it has the expected table structure
      if (typeof firstItem === "object") {
        // Get all unique keys from all objects in consistent order
        const allKeys = Array.from(
          new Set(data.flatMap((item) => Object.keys(item)))
        );

        return (
          <div className="copy-vs-manuscript-table">
            <table>
              <thead>
                <tr>
                  {allKeys.map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row: any, index: number) => (
                  <tr key={index}>
                    {allKeys.map((key) => {
                      const value = row[key];
                      const displayValue =
                        value === null || value === undefined
                          ? ""
                          : typeof value === "string"
                            ? value
                            : JSON.stringify(value);

                      // Handle multi-line values like "expected"
                      const isMultiline =
                        key === "expected" ||
                        key === "Expected" ||
                        (typeof displayValue === "string" &&
                          displayValue.includes("\n"));

                      return (
                        <td key={`${index}-${key}`}>
                          {isMultiline
                            ? displayValue
                                .split("\n")
                                .map((line: string, i: number) => (
                                  <div key={i}>{line}</div>
                                ))
                            : displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    }

    // Handle object-based JSON (e.g., copy_vs_manuscript with status sorting)
    if (typeof data === "object" && !Array.isArray(data)) {
      const entries = Object.entries(data);
      
      // Check if this looks like a copy_vs_manuscript object with status field
      if (
        entries.length > 0 &&
        typeof entries[0][1] === "object" &&
        (entries[0][1] as any).status
      ) {
        // Sort entries by status: Mismatch first, then Observation, then Match
        const statusOrder: Record<string, number> = {
          MISMATCH: 1,
          OBSERVATION: 2,
          MATCH: 3,
        };

        const sortedEntries = entries.sort(
          ([, detailsA]: [string, any], [, detailsB]: [string, any]) => {
            const statusA = detailsA.status || "";
            const statusB = detailsB.status || "";
            const orderA = statusOrder[statusA] || 999;
            const orderB = statusOrder[statusB] || 999;
            return orderA - orderB;
          },
        );

        return (
          <div className="copy-vs-manuscript-table">
            <table>
              <thead>
                <tr>
                  <th>Section Name</th>
                  <th>Status</th>
                  <th>Explanation</th>
                  <th>Expected Text</th>
                  <th>Actual Text</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map(([sectionName, details]: [string, any]) => (
                  <tr key={sectionName}>
                    <td>{sectionName}</td>
                    <td>{details.status || ""}</td>
                    <td>{details.explanation || ""}</td>
                    <td>{details.expected_text || ""}</td>
                    <td>{details.actual_text || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
    }
  } catch (error) {
    // Not JSON or parsing failed, fall through to formatMessage
  }

  // Default: format as regular message text
  return formatMessage(finding.message);
};

interface Finding {
  finding_id: string;
  check_id: string;
  category: string;
  result: "PASS" | "FAIL" | "BRAND_GUIDELINE_NOT_AVAILABLE" | "NA";
  severity: "INFO" | "MINOR" | "MAJOR" | "CRITICAL";
  message: string;
  frame_id: string | null;
  doc_id: string | null;
  source_type: string | null;
  actual: string | null;
  expected: string | null;
  recommendation: string | null;
  used_llm: boolean;
  confidence: number | null;
  location?: string | null;
}

interface FigmaSectionProps {
  figmaFrameFindings: Finding[];
  figmaFindings: Finding[];
  channel?: string;
  frameImages: Map<string, string>;
  expandedFrames: Set<string>;
  expandedFindings: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  isResultsComplete: boolean;
}

export const FigmaSection: React.FC<FigmaSectionProps> = ({
  figmaFrameFindings,
  figmaFindings,
  channel,
  frameImages,
  expandedFrames,
  expandedFindings,
  setExpandedFrames,
  setExpandedFindings,
  isResultsComplete,
}) => {
  // Determine if this is banner or non-banner based on channel
  const isBannerChannel =
    channel?.toLowerCase() === "banner" ||
    channel?.toLowerCase() === "animated_banner";

  // Use appropriate findings based on channel
  const currentFindings = isBannerChannel ? figmaFrameFindings : figmaFindings;

  if (currentFindings.length === 0) return null;

  // Determine which findings to show
  const selectedKey =
    expandedFrames.size > 0 ? Array.from(expandedFrames)[0] : null;
  let selectedFindings: Finding[] | null = null;
  let selectedFindingsComponent: React.ReactNode = null;

  if (selectedKey) {
    if (isBannerChannel) {
      // Banner: selectedKey could be frame_id or check_id
      if (!selectedKey.startsWith("check_")) {
        // Frame-based findings
        const frameId = selectedKey;
        const frameData = currentFindings.filter(
          (f) =>
            f.frame_id === frameId &&
            (f.check_id?.startsWith("figma_") ||
              f.source_type === "figma_frame"),
        );
        if (frameData.length > 0) {
          selectedFindings = frameData;
        }
      } else {
        // Check-based findings
        const checkId = selectedKey.replace("check_", "");
        const checkFindings = currentFindings.filter(
          (f) => f.check_id === checkId,
        );
        if (checkFindings.length > 0) {
          selectedFindingsComponent = (
            <CheckFindings findings={checkFindings} useLocation={false} isResultsComplete={isResultsComplete} />
          );
        }
      }
    } else {
      // Non-banner: selectedKey is check_id
      if (selectedKey.startsWith("check_")) {
        const checkId = selectedKey.replace("check_", "");
        const checkFindings = currentFindings.filter(
          (f) => f.check_id === checkId,
        );
        if (checkFindings.length > 0) {
          selectedFindingsComponent = (
            <CheckFindings
              findings={checkFindings}
              useLocation={channel === "animated_banner" ? false : true}
              isResultsComplete={isResultsComplete}
            />
          );
        }
      }
    }
  }

  // If we have selectedFindings but no specific component, use default
  if (selectedFindings && !selectedFindingsComponent) {
    selectedFindingsComponent = (
      <FindingsByStatus
        findings={selectedFindings}
        expandedFindings={expandedFindings}
        setExpandedFindings={setExpandedFindings}
        isResultsComplete={isResultsComplete}
      />
    );
  }

  return (
    <div className="card-based-section">
      <div className="cards-container-left">
        {isBannerChannel ? (
          <FigmaBannerFrames
            figmaFrameFindings={currentFindings}
            frameImages={frameImages}
            expandedFrames={expandedFrames}
            setExpandedFrames={setExpandedFrames}
            setExpandedFindings={setExpandedFindings}
          />
        ) : (
          <FigmaNonBannerChecks
            figmaFindings={currentFindings}
            expandedFrames={expandedFrames}
            setExpandedFrames={setExpandedFrames}
            setExpandedFindings={setExpandedFindings}
          />
        )}

        {!isResultsComplete && (
          <div style={{ textAlign: "center", color: "#999" }}>
            <div className="skeleton-finding-card">
              <div className="skeleton-label" style={{ marginBottom: "8px" }}></div>
              <div className="skeleton-text" style={{ width: "80%", marginBottom: "8px" }}></div>
              <div className="skeleton-text" style={{ width: "60%" }}></div>
            </div>
          </div>
        )}
      </div>

      {selectedFindingsComponent && (
        <div className="findings-container-right">
          {selectedFindingsComponent}
        </div>
      )}
    </div>
  );
};

// Banner channel Figma frames component
const FigmaBannerFrames: React.FC<{
  figmaFrameFindings: Finding[];
  frameImages: Map<string, string>;
  expandedFrames: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  figmaFrameFindings,
  frameImages,
  expandedFrames,
  setExpandedFrames,
  setExpandedFindings,
}) => {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  const handleImageClick = (
    e: React.MouseEvent,
    frameId: string,
    index: number,
  ) => {
    e.stopPropagation();
    setSelectedImageId(frameId);
    setSelectedImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImageId(null);
  };

  // Separate findings into frame-based and check-based
  const framesMap = new Map<
    string,
    { frame_id: string; doc_id: string; findings: Finding[] }
  >();
  const checkBasedFindings: Finding[] = [];

  figmaFrameFindings.forEach((finding) => {
    if (
      finding.frame_id &&
      finding.doc_id &&
      (finding.check_id?.startsWith("figma_") ||
        finding.source_type === "figma_frame")
    ) {
      // Frame-based figma findings
      if (!framesMap.has(finding.frame_id)) {
        framesMap.set(finding.frame_id, {
          frame_id: finding.frame_id,
          doc_id: finding.doc_id,
          findings: [],
        });
      }
      framesMap.get(finding.frame_id)!.findings.push(finding);
    } else {
      // Check-based findings (not matching figma condition)
      checkBasedFindings.push(finding);
    }
  });

  // Group check-based findings by check_id
  const findingsByCheckId = checkBasedFindings.reduce(
    (acc, finding) => {
      const checkId = finding.check_id || "unknown";
      if (!acc[checkId]) {
        acc[checkId] = [];
      }
      acc[checkId].push(finding);
      return acc;
    },
    {} as Record<string, Finding[]>,
  );

  const figmaFrames = Array.from(framesMap.values()).sort((a, b) => {
    const aMatch = a.frame_id.match(/_frame_(\d+)$/);
    const bMatch = b.frame_id.match(/_frame_(\d+)$/);
    const aNum = aMatch ? parseInt(aMatch[1], 10) : 0;
    const bNum = bMatch ? parseInt(bMatch[1], 10) : 0;
    return aNum - bNum;
  });

  // Only select if the selected ID is in this section
  const selectedFrameId =
    expandedFrames.size > 0 ? Array.from(expandedFrames)[0] : null;

  const handleCardSelect = (frameId: string) => {
    setExpandedFindings(new Set()); // Clear other section selections
    setExpandedFrames(new Set([frameId]));
  };

  return (
    <>
      {/* Frame-based cards */}
      {figmaFrames.map((frameData, index) => {
        const frameMatch = frameData.frame_id.match(/_frame_(\d+)$/);
        const frameNumber = frameMatch
          ? parseInt(frameMatch[1], 10)
          : index + 1;
        const displayLabel = `Frame ${frameNumber}`;
        const isSelected = frameData.frame_id === selectedFrameId;
        const imageUrl = frameImages.get(frameData.frame_id);

        const failedCount = frameData.findings.filter(
          (f) => f.result === "FAIL",
        ).length;
        const warningCount = frameData.findings.filter(
          (f) => f.result !== "PASS" && f.result !== "FAIL",
        ).length;

        return (
          <div
            key={frameData.frame_id}
            className={`frame-card ${isSelected ? "selected" : ""}`}
            onClick={() => handleCardSelect(frameData.frame_id)}
          >
            <div className="frame-card-content-wrapper">
              <div className="frame-card-image-section">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={displayLabel}
                    className="frame-card-image"
                    onClick={(e) => handleImageClick(e, frameData.frame_id, index)}
                    style={{ cursor: "pointer" }}
                  />
                ) : (
                  <div className="frame-card-placeholder">
                    <Icon type="document" size="medium" />
                  </div>
                )}
              </div>
              <div className="frame-card-text-section">
                <div className="frame-card-label">
                  Image {index + 1} of {figmaFrames.length}
                </div>
                <div className="frame-card-counts">
                  {warningCount > 0 && (
                    <span className="count-badge warning">{warningCount}</span>
                  )}
                  {failedCount > 0 && (
                    <span className="count-badge failed">{failedCount}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Check-based cards */}
      {Object.entries(findingsByCheckId).map(([checkId, checkFindings]) => {
        const checkKey = `check_${checkId}`;
        const isSelected = expandedFrames.has(checkKey);
        const failedCount = checkFindings.filter(
          (f) => f.result === "FAIL",
        ).length;
        const warningCount = checkFindings.filter(
          (f) => f.result !== "PASS" && f.result !== "FAIL",
        ).length;

        return (
          <div
            key={checkId}
            className={`frame-card check-card ${isSelected ? "selected" : ""}`}
            onClick={() => handleCardSelect(checkKey)}
          >
            <div className="check-card-body">
              <div className="check-card-title">{formatCheckId(checkId)}</div>
            </div>
            <div className="check-card-header">
              <div className="frame-card-counts">
                {warningCount > 0 && (
                  <span className="count-badge warning">{warningCount}</span>
                )}
                {failedCount > 0 && (
                  <span className="count-badge failed">{failedCount}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Image Preview Modal */}
      {selectedImageId && (
        <div
          className="image-preview-modal-overlay"
          onClick={closeModal}
        >
          <div
            className="image-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-preview-header">
              <div className="image-preview-title">
                Image {selectedImageIndex + 1} of {figmaFrames.length}
              </div>
              <button className="image-preview-close" onClick={closeModal}>
                <Icon type="close" size="small" />
              </button>
            </div>
            {frameImages.get(selectedImageId) && (
              <img
                src={frameImages.get(selectedImageId)!}
                alt="Full preview"
                className="image-preview-full"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Non-banner Figma checks component
const FigmaNonBannerChecks: React.FC<{
  figmaFindings: Finding[];
  expandedFrames: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  figmaFindings,
  expandedFrames,
  setExpandedFrames,
  setExpandedFindings,
}) => {
  // Group by check_id
  const findingsByCheckId = figmaFindings.reduce(
    (acc, finding) => {
      const checkId = finding.check_id || "unknown";
      if (!acc[checkId]) {
        acc[checkId] = [];
      }
      acc[checkId].push(finding);
      return acc;
    },
    {} as Record<string, Finding[]>,
  );

  return (
    <>
      {Object.entries(findingsByCheckId).map(([checkId, checkFindings]) => (
        <FigmaCheckCard
          key={checkId}
          checkId={checkId}
          checkFindings={checkFindings}
          expandedFrames={expandedFrames}
          setExpandedFrames={setExpandedFrames}
          setExpandedFindings={setExpandedFindings}
        />
      ))}
    </>
  );
};

// Component to handle each check_id group
const FigmaCheckCard: React.FC<{
  checkId: string;
  checkFindings: Finding[];
  expandedFrames: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  checkId,
  checkFindings,
  expandedFrames,
  setExpandedFrames,
  setExpandedFindings,
}) => {
  const checkKey = `check_${checkId}`;

  // Only select if this check is selected
  const isSelected = expandedFrames.has(checkKey);

  const failedCount = checkFindings.filter((f) => f.result === "FAIL").length;
  const warningCount = checkFindings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  ).length;

  // Get description from first finding's message or category
  const description = null;

  const handleCardSelect = () => {
    setExpandedFindings(new Set()); // Clear other section selections
    setExpandedFrames(new Set([checkKey]));
  };

  return (
    <div
      className={`frame-card check-card ${isSelected ? "selected" : ""}`}
      onClick={handleCardSelect}
    >
      <div className="check-card-body">
        <div className="check-card-title">{formatCheckId(checkId)}</div>
        {description && (
          <div className="check-card-description">{description}</div>
        )}
      </div>
      <div className="check-card-header">
        <div className="frame-card-counts">
          {warningCount > 0 && (
            <span className="count-badge warning">{warningCount}</span>
          )}
          {failedCount > 0 && (
            <span className="count-badge failed">{failedCount}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Findings by status component - groups findings by FAIL, warnings, PASS
const FindingsByStatus: React.FC<{
  findings: Finding[];
  expandedFindings: Set<string>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  isResultsComplete?: boolean;
}> = ({
  findings,
  expandedFindings: _expandedFindings,
  setExpandedFindings: _setExpandedFindings,
  isResultsComplete = true,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["failed", "warnings"]),
  );

  const failedFindings = findings.filter((f) => f.result === "FAIL");
  const warningFindings = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  );
  const passedFindings = findings.filter((f) => f.result === "PASS");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const renderFindingsList = (findingsList: Finding[], offset: number) => {
    return findingsList.map((finding, index) => (
      <div key={finding.finding_id} className="status-finding-item">
        <div className="finding-header">
          <span className="finding-title">
            Check {offset + index + 1}: {formatCheckId(finding.check_id)}
          </span>
        </div>
        <div className="finding-content">
          {renderFindingMessage(finding)}
          {(finding.actual || finding.expected || finding.recommendation) && (
            <div className="finding-recommendation">
              {finding.recommendation || finding.expected || finding.actual}
            </div>
          )}
        </div>
      </div>
    ));
  };

  return (
    <div className="findings-by-status">
      {failedFindings.length > 0 && (
        <div className="status-section failed">
          <div
            className="status-header"
            onClick={() => toggleSection("failed")}
          >
            <Icon
              type={
                expandedSections.has("failed") ? "chevron-up" : "chevron-down"
              }
              size="small"
            />
            <FailIcon className="status-icon" />
            <span className="status-title">
              Failed ({failedFindings.length})
              {!isResultsComplete && (
                <Icon
                  type="loading"
                  size="small"
                  className="spinner-loader"
                />
              )}
            </span>
          </div>
          {expandedSections.has("failed") && (
            <div className="status-content">
              {renderFindingsList(failedFindings, 0)}
            </div>
          )}
        </div>
      )}

      {warningFindings.length > 0 && (
        <div className="status-section warnings">
          <div
            className="status-header"
            onClick={() => toggleSection("warnings")}
          >
            <Icon
              type={
                expandedSections.has("warnings") ? "chevron-up" : "chevron-down"
              }
              size="small"
            />
            <WarningIcon className="status-icon" />
            <span className="status-title">
              Observations ({warningFindings.length})
              {!isResultsComplete && (
                <Icon
                  type="loading"
                  size="small"
                  className="spinner-loader"
                />
              )}
            </span>
          </div>
          {expandedSections.has("warnings") && (
            <div className="status-content">
              {renderFindingsList(warningFindings, failedFindings.length)}
            </div>
          )}
        </div>
      )}

      {passedFindings.length > 0 && (
        <div className="status-section passed">
          <div
            className="status-header"
            onClick={() => toggleSection("passed")}
          >
            <Icon
              type={
                expandedSections.has("passed") ? "chevron-up" : "chevron-down"
              }
              size="small"
            />
            <Icon type="check-round" size="small" className="status-icon" />
            <span className="status-title">
              Passed ({passedFindings.length})
              {!isResultsComplete && (
                <Icon
                  type="loading"
                  size="small"
                  className="spinner-loader"
                />
              )}
            </span>
          </div>
          {expandedSections.has("passed") && (
            <div className="status-content">
              {renderFindingsList(
                passedFindings,
                failedFindings.length + warningFindings.length,
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Check findings component - shows findings grouped by status with location
const CheckFindings: React.FC<{
  findings: Finding[];
  useLocation?: boolean;
  isResultsComplete?: boolean;
}> = ({ findings, useLocation = false, isResultsComplete = true }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["failed", "warnings"]),
  );

  const failedFindings = findings.filter((f) => f.result === "FAIL");
  const warningFindings = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  );
  const passedFindings = findings.filter((f) => f.result === "PASS");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const renderFindingsList = (findingsList: Finding[]) => {
    return findingsList.map((finding) => {
      const displayValue = useLocation ? finding.location : finding.frame_id;
      console.log("Finding:", useLocation, displayValue, finding.frame_id);
      return (
        <div key={finding.finding_id} className="status-finding-item">
          <div className="finding-header">
            <span className="finding-title">
              {/* {displayValue && (
                <> */}
                  Section: <span>{finding.frame_id}</span>
                {/* </>
              )} */}
            </span>
          </div>
          <div className="finding-content">
            {renderFindingMessage(finding)}
            {(finding.actual || finding.expected || finding.recommendation) && (
              <div className="finding-recommendation">
                {finding.recommendation || finding.expected || finding.actual}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="findings-by-status">
      {failedFindings.length > 0 && (
        <div className="status-section failed">
          <div
            className="status-header"
            onClick={() => toggleSection("failed")}
          >
            <Icon
              type={
                expandedSections.has("failed") ? "chevron-up" : "chevron-down"
              }
              size="small"
            />
            <FailIcon className="status-icon" />
            <span className="status-title">
              Failed ({failedFindings.length})
              {!isResultsComplete && (
                <Icon
                  type="loading"
                  size="small"
                  className="spinner-loader"
                />
              )}
            </span>
          </div>
          {expandedSections.has("failed") && (
            <div className="status-content">
              {renderFindingsList(failedFindings)}
            </div>
          )}
        </div>
      )}

      {warningFindings.length > 0 && (
        <div className="status-section warnings">
          <div
            className="status-header"
            onClick={() => toggleSection("warnings")}
          >
            <Icon
              type={
                expandedSections.has("warnings") ? "chevron-up" : "chevron-down"
              }
              size="small"
            />
            <WarningIcon className="status-icon" />
            <span className="status-title">
              Observations ({warningFindings.length})
              {!isResultsComplete && (
                <Icon
                  type="loading"
                  size="small"
                  className="spinner-loader"
                />
              )}
            </span>
          </div>
          {expandedSections.has("warnings") && (
            <div className="status-content">
              {renderFindingsList(warningFindings)}
            </div>
          )}
        </div>
      )}

      {passedFindings.length > 0 && (
        <div className="status-section passed">
          <div
            className="status-header"
            onClick={() => toggleSection("passed")}
          >
            <Icon
              type={
                expandedSections.has("passed") ? "chevron-up" : "chevron-down"
              }
              size="small"
            />
            <Icon type="check-round" size="small" className="status-icon" />
            <span className="status-title">
              Passed ({passedFindings.length})
              {!isResultsComplete && (
                <Icon
                  type="loading"
                  size="small"
                  className="spinner-loader"
                />
              )}
            </span>
          </div>
          {expandedSections.has("passed") && (
            <div className="status-content">
              {renderFindingsList(passedFindings)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
